import {
  Link,
  type AnyRoute,
  type LinkProps,
  useMatches,
  useParams,
  useRouter,
} from '@tanstack/react-router';
import {
  ChevronsUpDownIcon,
  FolderClosedIcon,
  FolderPlusIcon,
  SearchIcon,
} from 'lucide-react';
import {useTranslation} from 'react-i18next';
import {CreateMatterDialog} from '@/components/matter/CreateMatterDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {useCanCreateMatter} from '@/hooks/useCanCreateMatter';
import useMatter from '@/hooks/useMatter';
import useMatters from '@/hooks/useMatters';
import {useCaseContext} from '../contexts/CaseContext';
import {useRef, useState} from "react";

const DEFAULT_MATTER_SECTION = 'case-files';
const MATTER_ROUTE_PREFIX = '/matter/$slug';

interface MatterDropdownProps {
  lightTrigger?: boolean;
}

const hasOnlyMatterSlugParam = (routePath: string): boolean => {
  const paramNames = [...routePath.matchAll(/\$([A-Za-z0-9_]+)/g)].map((match) => match[1]);
  return paramNames.every((name) => name === 'slug');
};

const isRoutePrefix = (candidate: string, routePath: string): boolean => {
  return routePath === candidate || routePath.startsWith(`${candidate}/`);
};

const getMatterSuffixPath = (leafRoutePath: string | undefined, routes: AnyRoute[]): string => {
  if (!leafRoutePath?.startsWith(`${MATTER_ROUTE_PREFIX}/`)) return DEFAULT_MATTER_SECTION;

  const staticMatterRoute = routes
    .filter((route) => {
      const routePath = route.fullPath;
      return (
        routePath !== MATTER_ROUTE_PREFIX &&
        routePath.startsWith(`${MATTER_ROUTE_PREFIX}/`) &&
        hasOnlyMatterSlugParam(routePath) &&
        isRoutePrefix(routePath, leafRoutePath)
      );
    })
    .sort((a, b) => b.fullPath.length - a.fullPath.length)[0];

  return staticMatterRoute?.fullPath.slice(`${MATTER_ROUTE_PREFIX}/`.length) ?? DEFAULT_MATTER_SECTION;
};

const MatterDropdown = ({ lightTrigger = false }: MatterDropdownProps) => {
  const {matters, isLoading} = useMatters(false);
  const {t} = useTranslation('common');
  const {selectedCase} = useCaseContext();
  const {slug: routeSlug} = useParams({strict: false});
  const router = useRouter();
  const leafRoutePath = useMatches({
    select: (matches) => matches[matches.length - 1]?.fullPath,
  });
  // Prefer the URL slug so the trigger stays in sync with the page even when
  // CaseContext is stale or the matter is missing from the active-matters list
  // (archived matters, or a landing URL before the sidebar effect runs).
  const matterGuid =
    typeof routeSlug === 'string' && routeSlug && routeSlug !== 'undefined'
      ? routeSlug
      : selectedCase;
  const {matter: routeMatter, isLoading: isMatterLoading} = useMatter(matterGuid);
  const [createMatterDialogOpen, setCreateMatterDialogOpen] = useState(false);
  const canCreateMatter = useCanCreateMatter();
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Preserve the deepest registered static matter route. Routes with params
  // beyond $slug point at matter-specific resources and fall back to their
  // nearest static parent section.
  const buildMatterPath = (newSlug: string): LinkProps['to'] => {
    return `/matter/${newSlug}/${getMatterSuffixPath(
      leafRoutePath,
      Object.values(router.routesByPath)
    )}` as LinkProps['to'];
  };

  const currentMatter = (matters || []).find(
    (matter) => matter.guid === matterGuid
  );
  const currentMatterName = currentMatter?.name || routeMatter?.name;
  // Active-list loading is not enough: archived / URL-only matters resolve via
  // useMatter, and the list is often already cached when that fetch starts.
  const isTriggerLoading = isLoading || Boolean(matterGuid && isMatterLoading);

  const sortedMatters = (matters || [])
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredMatters = normalizedQuery
    ? sortedMatters.filter((matter) => {
        const name = matter.name.toLowerCase();
        const number = (matter.matter_no || '').toLowerCase();
        return (
          name.includes(normalizedQuery) || number.includes(normalizedQuery)
        );
      })
    : sortedMatters;
  const triggerClassName = lightTrigger
    ? 'bg-white border border-[hsl(217,47%,20%)] flex h-9 w-full items-center pl-2.5 pr-1.5 justify-between cursor-pointer rounded-lg text-[13px] font-medium text-[hsl(217,47%,20%)] hover:bg-zinc-50 hover:border-[hsl(217,47%,28%)] transition-all duration-200'
    : 'bg-white/10 border border-white/15 flex h-9 w-full items-center pl-2.5 pr-1.5 justify-between cursor-pointer rounded-lg text-[13px] font-medium text-white hover:bg-white/15 hover:border-white/25 transition-all duration-200';
  const triggerIconClassName = lightTrigger
    ? 'w-4 h-4 ml-1 shrink-0 text-[hsl(217,47%,20%)]'
    : 'w-4 h-4 ml-1 shrink-0 text-zinc-300';

  return (
    <>
      <DropdownMenu
        onOpenChange={(open) => {
          if (!open) setSearchQuery('');
        }}
      >
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={triggerClassName}
            title={currentMatterName || ''}
          >
            <span className="flex-1 truncate text-left">
              {currentMatterName
                ? currentMatterName
                : isTriggerLoading
                  ? 'Loading matters...'
                  : 'Select a matter'}
            </span>
            <ChevronsUpDownIcon className={triggerIconClassName}/>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-(--radix-dropdown-menu-trigger-width) p-1.5 rounded-lg border-white/10 bg-[hsl(221,36%,20%)] text-white shadow-lg"
          onOpenAutoFocus={(e) => {
            // Focus the search input on open instead of the first menu item
            // so the user can immediately start typing to filter.
            e.preventDefault();
            searchInputRef.current?.focus();
          }}
        >
          {isLoading ? (
            <div className="text-[13px] py-2 px-2.5 text-zinc-300">
              Loading matters...
            </div>
          ) : (
            <>
              <DropdownMenuLabel
                className="text-[10px] font-semibold uppercase tracking-wider text-zinc-300 py-1.5 px-2.5">
                {t('matters')}
              </DropdownMenuLabel>
              {sortedMatters.length > 0 && (
                <div className="px-1.5 pb-1.5">
                  <div className="relative">
                    <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-300 pointer-events-none" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        // Let Escape bubble up so Radix can close the dropdown;
                        // swallow everything else so Radix's built-in typeahead
                        // and Enter/Space item activation don't fight the input.
                        if (e.key !== 'Escape') {
                          e.stopPropagation();
                        }
                      }}
                      placeholder="Search matters..."
                      className="w-full h-7 pl-7 pr-2 rounded-md border border-white/15 bg-white/5 text-[12px] text-white placeholder:text-zinc-300 focus:outline-none focus:ring-1 focus:ring-white/30 focus:border-white/30"
                    />
                  </div>
                </div>
              )}
              <div
                className={
                  filteredMatters.length > 10
                    ? 'max-h-[40vh] overflow-y-auto scrollbar'
                    : undefined
                }
              >
                {filteredMatters.length === 0 && (
                  <div className="text-[12px] py-2 px-2.5 text-zinc-300">
                    No matters found
                  </div>
                )}
                {filteredMatters.map((matter) => (
                  <DropdownMenuItem
                    key={matter.guid}
                    asChild
                    className="cursor-pointer hover:bg-white/10 focus:bg-white/10 transition-colors duration-150"
                  >
                    <Link
                      to={buildMatterPath(matter.guid)}
                      className="flex py-1.5 px-2.5 rounded-md text-[13px] font-medium text-zinc-100 gap-2 items-center hover:text-white focus:text-white"
                      title={
                        matter.matter_no
                          ? `${matter.name} (${matter.matter_no})`
                          : matter.name
                      }
                    >
                      <FolderClosedIcon className="w-4 h-4 shrink-0 text-zinc-300"/>
                      <div className="flex flex-col min-w-0 flex-1 leading-tight">
                        <span className="truncate">{matter.name}</span>
                        {matter.matter_no && (
                          <span className="truncate text-[11px] font-normal text-zinc-300">
                            {matter.matter_no}
                          </span>
                        )}
                      </div>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </div>
              {canCreateMatter && (
                <>
                  <DropdownMenuSeparator className="bg-white/10"/>
                  <DropdownMenuItem
                    onSelect={() => {
                      // Defer to let the dropdown finish closing before the dialog
                      // mounts — otherwise Radix's pointer-events lock can leak and
                      // freeze the page after the dialog closes.
                      setTimeout(() => setCreateMatterDialogOpen(true), 0);
                    }}
                    className="flex py-1.5 px-2.5 rounded-md text-[13px] font-medium text-zinc-100 gap-2 items-center cursor-pointer hover:bg-white/10 focus:bg-white/10 hover:text-white focus:text-white transition-colors duration-150"
                  >
                    <FolderPlusIcon className="w-4 h-4 shrink-0 text-zinc-300"/>
                    <span className="truncate">{t('new-matter')}</span>
                  </DropdownMenuItem>
                </>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <CreateMatterDialog
        isOpen={createMatterDialogOpen}
        onClose={() => setCreateMatterDialogOpen(false)}
      />
    </>
  );
};

export default MatterDropdown;
